import {Text} from '@dropbox/dig-components/typography';
import cx from 'classnames';
import {Table} from 'components/DSYS/Table';
import {
  findTableLayoutElement,
  getTableScrollContainerElement,
  getTableWrapperElement,
  GROUP_STICKY_HEADER_Z_INDEX,
  isPageScrollStickyTable,
  observeStickyLayout,
} from 'components/DSYS/tableStickyLayout';
import {type CSSProperties, type ReactNode, useEffect, useState} from 'react';

import styles from './TableGroupHeaderSection.module.css';

export {styles as tableGroupHeaderStyles};

/**
 * - `clone` (default): JS scroll listener renders a `position: fixed` clone. Required for
 *   virtualized tables (Workstreams) where the header DOM is recycled out of view.
 * - `native`: pure CSS `position: sticky`, offset below the column header. Use for
 *   non-virtualized page-scroll tables.
 */
type GroupHeaderStickyMode = 'clone' | 'native';

type TableGroupHeaderSectionProps = {
  sectionKey: string;
  label: ReactNode;
  columnCount: number;
  children: ReactNode;
  compactHeaderPadding?: boolean;
  stickyMode?: GroupHeaderStickyMode;
  'data-testid'?: string;
  onStickyChange?: (sectionKey: string, stickyHeight: number) => void;
};

export const TableGroupHeaderSection = ({
  sectionKey,
  label,
  columnCount,
  children,
  compactHeaderPadding = false,
  stickyMode = 'clone',
  'data-testid': dataTestId,
  onStickyChange,
}: TableGroupHeaderSectionProps) => {
  const isNativeSticky = stickyMode === 'native';
  const [headerSentinel, setHeaderSentinel] = useState<HTMLDivElement | null>(null);
  const [endMarker, setEndMarker] = useState<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [stickyStyle, setStickyStyle] = useState<CSSProperties>({});

  useEffect(() => {
    // Native sticky is handled purely in CSS — skip the scroll-listener clone machinery.
    if (isNativeSticky) return;
    if (!headerSentinel || !endMarker) return;

    const tableLayoutElement = findTableLayoutElement(headerSentinel);
    const tableWrapper = getTableWrapperElement(tableLayoutElement);
    const tableContainer = getTableScrollContainerElement(tableLayoutElement);
    if (!tableWrapper || !tableContainer) return;

    const isPageScrollTable = isPageScrollStickyTable(tableLayoutElement);
    const frameElement = tableLayoutElement?.querySelector(
      '[class*="pageScrollTableFrame"]'
    ) as HTMLElement | null;

    const handleScroll = () => {
      const headerRect = headerSentinel.getBoundingClientRect();
      const endMarkerRect = endMarker.getBoundingClientRect();
      const layoutRect = (frameElement ?? tableWrapper).getBoundingClientRect();
      const headerCellRect = headerSentinel.parentElement?.getBoundingClientRect();
      const tableHeaderCell = tableWrapper.querySelector(
        '[class*="headerCell"]'
      ) as HTMLElement | null;
      const tableHeaderCellRect = tableHeaderCell?.getBoundingClientRect();
      const stickyTop = tableHeaderCellRect?.bottom ?? layoutRect.top + 40;
      const headerHeight = headerCellRect?.height ?? 0;
      const shouldBeSticky =
        headerRect.top < stickyTop && endMarkerRect.top > stickyTop + headerHeight;

      if (shouldBeSticky) {
        setIsSticky(true);
        setStickyStyle({
          position: 'fixed',
          top: stickyTop,
          left: layoutRect.left,
          width: layoutRect.width,
          zIndex: GROUP_STICKY_HEADER_Z_INDEX + 2,
        });
      } else {
        setIsSticky(false);
        setStickyStyle({});
      }

      onStickyChange?.(sectionKey, shouldBeSticky ? headerHeight : 0);
    };

    return observeStickyLayout({
      observedElements: [tableWrapper, tableContainer, headerSentinel.parentElement, frameElement],
      onLayoutChange: handleScroll,
      tableContainer,
      scrollTarget: isPageScrollTable ? window : undefined,
    });
  }, [headerSentinel, endMarker, onStickyChange, sectionKey, isNativeSticky]);

  useEffect(() => {
    return () => {
      onStickyChange?.(sectionKey, 0);
    };
  }, [onStickyChange, sectionKey]);

  const renderHeaderContent = (hidden = false) => (
    <div
      className={cx(styles.groupHeaderContent, {
        [styles.groupHeaderContentCompact]: compactHeaderPadding,
      })}
      // Hide the in-flow header (keep its space) while the clone is shown so it isn't
      // drawn twice. Native sticky has a single header and never sets this.
      style={hidden ? {visibility: 'hidden'} : undefined}
      aria-hidden={hidden || undefined}
    >
      <Text isBold size="small">
        {label}
      </Text>
    </div>
  );

  return (
    <>
      <Table.Row
        className={cx(styles.groupHeaderRow, {
          [styles.groupHeaderRowNativeSticky]: isNativeSticky,
        })}
        data-testid={dataTestId ?? `table-group-header-${sectionKey}`}
      >
        <Table.Cell colSpan={columnCount} className={styles.groupHeaderCell} style={{padding: 0}}>
          {isNativeSticky ? null : (
            <div
              ref={setHeaderSentinel}
              style={{position: 'absolute', top: 0, left: 0, right: 0}}
            />
          )}
          {renderHeaderContent(!isNativeSticky && isSticky)}
        </Table.Cell>
      </Table.Row>

      {!isNativeSticky && isSticky ? (
        <div className={styles.groupStickyHeader} style={stickyStyle}>
          {renderHeaderContent()}
        </div>
      ) : null}

      {children}

      {isNativeSticky ? null : <div ref={setEndMarker} className={styles.sectionEndMarker} />}
    </>
  );
};
